// The Templates tab's container: the agent/team-narrowed list, delete, and the
// per-row batch lineage. Authoring is NOT here — it moved to the
// /simulations/templates/* Authoring routes (UI-LAW §4a), so this file owns no
// save mutation, no agent-option plumbing, and no drawer state. Navigation to
// those routes is the parent screen's job; this container only reports which
// row was chosen.
import { useMemo, useState } from "react";

import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import { useResolvedWorkingTeamId } from "../../lib/workingScope";
import { useListQuery } from "../../lib/useListQuery";
import { useToast } from "../../ui";
import { TemplatesListView } from "./TemplatesListView";
import { TemplateBatchLineage } from "./TemplateBatchLineage";

type TemplateResponse = components["schemas"]["TemplateResponse"];

// Schedule transitions have no SSE invalidation (failed fires have no batch),
// so refresh the mounted list at a slow bounded cadence.
export const TEMPLATE_SCHEDULE_REFETCH_INTERVAL_MS = 30_000;

export interface TemplatesListProps {
  onOpenEdit: (templateId: string) => void;
  onOpenInTranscripts: (batchId: string) => void;
  onRun: (templateId: string, agentId: string) => void;
  // The working agent scope narrows the server-side template listing; All
  // agents narrows to the working team via `team_id` (docs/09 §4).
  scopedAgentId?: string;
}

export function TemplatesList({
  onOpenEdit,
  onOpenInTranscripts,
  onRun,
  scopedAgentId,
}: TemplatesListProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [deleteTemplateId, setDeleteTemplateId] = useState<string | null>(null);
  const [expandedTemplateId, setExpandedTemplateId] = useState<string | null>(
    null,
  );
  const pauseSchedule = useMutation({
    mutationFn: async (template: TemplateResponse) => {
      const { data, error } = await apiClient.POST(
        "/v1/templates/{id}:pause-schedule",
        { params: { path: { id: template.id } } },
      );
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onError: (error) => {
      toast.error("Schedule pause failed", {
        description: requestErrorDescription(error),
      });
    },
    onSuccess: async (template) => {
      queryClient.setQueryData(
        queryKeys.templates.detail(template.id),
        template,
      );
      toast.success("Schedule paused");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.templates.all,
      });
    },
  });
  const resumeSchedule = useMutation({
    mutationFn: async (template: TemplateResponse) => {
      const { data, error } = await apiClient.POST(
        "/v1/templates/{id}:resume-schedule",
        { params: { path: { id: template.id } } },
      );
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onError: (error) => {
      toast.error("Schedule resume failed", {
        description: requestErrorDescription(error),
      });
    },
    onSuccess: async (template) => {
      queryClient.setQueryData(
        queryKeys.templates.detail(template.id),
        template,
      );
      toast.success("Schedule resumed");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.templates.all,
      });
    },
  });
  // All agents = the working team's templates (docs/09 §4); held until the
  // shell resolves the team. Agent wins: never both filters.
  const workingTeamId = useResolvedWorkingTeamId();
  const templates = useListQuery("/v1/templates", {
    enabled: scopedAgentId != null || workingTeamId !== undefined,
    params:
      scopedAgentId != null
        ? { agent_id: scopedAgentId }
        : { team_id: workingTeamId },
    queryKey:
      scopedAgentId != null
        ? queryKeys.templates.list({ agent_id: scopedAgentId })
        : queryKeys.templates.list({ team_id: workingTeamId }),
    refetchInterval: TEMPLATE_SCHEDULE_REFETCH_INTERVAL_MS,
  });
  const agents = useListQuery("/v1/agents", {
    pageSize: 200,
    queryKey: queryKeys.agents.list(),
  });
  const loadedAgentIds = useMemo(
    () => new Set(agents.items.map((agent) => agent.id)),
    [agents.items],
  );
  // A template can own an agent outside the loaded page (All agents lists
  // across teams); fetch just those so the Agent column is never a raw id.
  const missingAgentIds = useMemo(
    () =>
      [...new Set(templates.items.map((template) => template.agent_id))].filter(
        (agentId) => !loadedAgentIds.has(agentId),
      ),
    [loadedAgentIds, templates.items],
  );
  const referencedAgentQueries = useQueries({
    queries: missingAgentIds.map((agentId) => ({
      queryKey: queryKeys.agents.detail(agentId),
      queryFn: async () => {
        const { data, error } = await apiClient.GET("/v1/agents/{id}", {
          params: { path: { id: agentId } },
        });
        if (error) {
          throw new ApiRequestError(error);
        }
        return data;
      },
    })),
  });
  const agentNamesById = useMemo(() => {
    const names = new Map(
      agents.items.map((agent) => [agent.id, agent.display_name]),
    );
    for (const query of referencedAgentQueries) {
      if (query.data) {
        names.set(query.data.id, query.data.display_name);
      }
    }
    return names;
  }, [agents.items, referencedAgentQueries]);

  const deleteTemplate = useMutation({
    mutationFn: async (template: TemplateResponse) => {
      const { error } = await apiClient.DELETE("/v1/templates/{id}", {
        params: { path: { id: template.id } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return template;
    },
    onSuccess: async () => {
      setDeleteTemplateId(null);
      toast.success("Template deleted");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.templates.all,
      });
    },
    onError: (error) => {
      toast.error("Template delete failed", {
        description: requestErrorDescription(error),
      });
    },
  });

  return (
    <TemplatesListView
      agentNamesById={agentNamesById}
      deleteError={
        deleteTemplate.isError
          ? requestErrorDescription(deleteTemplate.error)
          : null
      }
      deleteTemplateId={deleteTemplateId}
      expandedTemplateId={expandedTemplateId}
      isDeletePending={deleteTemplate.isPending}
      pausedScheduleTemplateId={
        pauseSchedule.isPending ? pauseSchedule.variables.id : null
      }
      resumedScheduleTemplateId={
        resumeSchedule.isPending ? resumeSchedule.variables.id : null
      }
      templates={templates}
      onCancelDelete={() => {
        deleteTemplate.reset();
        setDeleteTemplateId(null);
      }}
      onConfirmDelete={(template) => {
        deleteTemplate.mutate(template);
      }}
      onOpenEdit={onOpenEdit}
      onOpenInTranscripts={onOpenInTranscripts}
      onPauseSchedule={(template) => {
        pauseSchedule.mutate(template);
      }}
      onResumeSchedule={(template) => {
        resumeSchedule.mutate(template);
      }}
      onRun={onRun}
      onStartDelete={(template) => {
        deleteTemplate.reset();
        setDeleteTemplateId(template.id);
      }}
      onToggleLineage={(templateId) => {
        setExpandedTemplateId((current) =>
          current === templateId ? null : templateId,
        );
      }}
      renderLineage={(template) => (
        <TemplateBatchLineage
          agentId={template.agent_id}
          templateId={template.id}
        />
      )}
    />
  );
}
